/**
 * Where WorkOS sends the browser once a session is ended.
 *
 * Without `returnTo`, AuthKit's logout URL carries no `return_to` and WorkOS
 * falls back to the application's "App homepage URL". With none configured the
 * person lands on error.workos.com/…/app-homepage-url-not-found ("Couldn't
 * sign in") instead of back on the app. Naming our own origin brings them home,
 * where the app starts a fresh sign-in with its own redirect URI.
 *
 * It also lets a sign-out without a live session still leave: AuthKit throws
 * `NoSessionError` then, unless a `returnTo` is given.
 *
 * The origin must be listed under the WorkOS application's sign-out redirects.
 */
export function signOutOptions(): { returnTo: string } {
	return { returnTo: `${window.location.origin}/` };
}

/**
 * Signs out here before signing out at the identity provider — the one
 * sequence every app's Sign out runs (ADJ-M03-4).
 *
 * Ours first, and it matters which way round: denylisting the session is what
 * makes the token stop working on every instance within the minute, and
 * `signOut` redirects away — so a failure after the redirect is a failure
 * nobody is left to notice.
 *
 * A failure to reach our own endpoint does not stop the sign-out. The person
 * asked to leave and must leave; the token expires on its own within fifteen
 * minutes, which is a far smaller problem than a Sign out button that does
 * nothing when the API is unwell.
 */
export async function endSession(
	api: { logout(): Promise<void> },
	signOut: (options: { returnTo: string }) => void | Promise<void>,
): Promise<void> {
	try {
		await api.logout();
	} catch {
		// Deliberately swallowed; see above.
	}
	await signOut(signOutOptions());
}
